<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, watch, inject } from "vue"
import type { Address } from "@types"
import type { Services } from "@services"
import type { AutoCompleteCompleteEvent } from "primevue/autocomplete"

// ================================
// TYPES & INTERFACES
// ================================
export interface CompanyFormValue {
    _id?: string
    name: string
    type?: string
    address?: Address | null
    idcompany?: string
    legalForm?: string
}

interface Suggestion {
    value: string
    name: string
    company: CompanyFormValue
}

interface Props {
    /**
     * Valeur de l'entreprise (v-model)
     */
    modelValue?: CompanyFormValue | null
    /**
     * Identifiant du champ
     */
    inputId?: string
    /**
     * Label du champ
     */
    label?: string
    /**
     * Message d'aide
     */
    hint?: string
    /**
     * Message d'erreur
     */
    error?: string
    /**
     * Champ obligatoire
     */
    required?: boolean
    /**
     * Champ désactivé
     */
    disabled?: boolean
    /**
     * Champ en lecture seule
     */
    readonly?: boolean
}

interface Emits {
    /**
     * Émis lors du changement de valeur
     */
    (e: "update:modelValue", value: CompanyFormValue | null): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    modelValue: null,
    inputId: "input-company-siren",
    label: "",
    hint: "",
    error: "",
    required: false,
    disabled: false,
    readonly: false,
})

const emit = defineEmits<Emits>()

// ================================
// SERVICES
// ================================
const services = inject<Services>("services")

// ================================
// ÉTAT
// ================================

/**
 * Valeur sélectionnée dans l'autocomplete
 */
const selectedValue = ref<Suggestion | string | null>(null)

/**
 * Liste des suggestions
 */
const suggestions = ref<Suggestion[]>([])

/**
 * Indique si la recherche est en cours
 */
const isLoading = ref(false)

/**
 * AbortController pour annuler les requêtes
 */
const controller = ref<AbortController | null>(null)

// ================================
// WATCHERS
// ================================

/**
 * Synchronise la valeur sélectionnée avec le modelValue
 */
watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue?.idcompany) {
            // Si on a une entreprise avec un idcompany, on crée un objet Suggestion compatible
            selectedValue.value = {
                value: newValue.idcompany,
                name: newValue.idcompany,
                company: newValue,
            }
        } else {
            selectedValue.value = null
        }
    },
    { immediate: true }
)

// ================================
// LOGIQUE MÉTIER
// ================================

/**
 * Recherche des entreprises par SIREN via l'API
 * @param event - Événement de complétion automatique
 */
async function onSearch(event: AutoCompleteCompleteEvent) {
    const query = event.query

    // Met à jour l'idcompany dans le modèle pendant la saisie
    emit("update:modelValue", {
        ...props.modelValue,
        name: props.modelValue?.name || "",
        idcompany: query,
    })

    if (!query || query.length < 3) {
        suggestions.value = []
        return
    }

    // Annuler la requête précédente
    controller.value?.abort()
    controller.value = new AbortController()

    isLoading.value = true

    try {
        if (!services?.$getCompanyBySiren) {
            console.warn("Service $getCompanyBySiren non disponible")
            return
        }

        const result = await services.$getCompanyBySiren(
            query,
            controller.value
        )

        if (result) {
            suggestions.value = [
                {
                    value: result.siren || query,
                    name: result.siren || query,
                    company: {
                        name: result.name || "",
                        type: "entreprise",
                        address: result.address,
                        idcompany: result.siren,
                    },
                },
            ]
        } else {
            suggestions.value = []
        }
    } catch (error: any) {
        if (error.name !== "AbortError") {
            console.error("Erreur lors de la recherche par SIREN:", error)
        }
        suggestions.value = []
    } finally {
        isLoading.value = false
    }
}

/**
 * Formate une adresse en chaîne de caractères
 * @param address - Adresse à formater
 * @returns Adresse formatée
 */
function formatAddress(address?: Address | null): string {
    if (!address) return ""

    const parts: string[] = []

    if (address.street) {
        parts.push(address.street)
    }

    if (address.zipcode || address.city) {
        const cityPart = [address.zipcode, address.city]
            .filter(Boolean)
            .join(" ")
        parts.push(cityPart)
    }

    return parts.join(", ")
}

// ================================
// INTERACTIONS
// ================================

/**
 * Sélectionne une entreprise dans les suggestions
 * @param suggestion - Suggestion sélectionnée
 */
function onSelectCompany(suggestion: Suggestion) {
    // Concatène l'adresse en inline_name si elle existe
    const address = suggestion.company.address
        ? {
              ...suggestion.company.address,
              inline_name: formatAddress(suggestion.company.address),
          }
        : null

    emit("update:modelValue", {
        ...suggestion.company,
        address,
    })
}

/**
 * Efface la valeur
 */
function onClear() {
    selectedValue.value = null
    suggestions.value = []
    emit("update:modelValue", {
        ...props.modelValue,
        name: props.modelValue?.name || "",
        idcompany: "",
    })
}
</script>

<template>
    <BaseInputWrapper
        :input-id="inputId"
        :label="label"
        :hint="hint"
        :error="error"
        :required="required"
    >
        <!-- ================================ -->
        <!-- AUTOCOMPLETE PRIMEVUE -->
        <!-- ================================ -->
        <BaseAutoComplete
            v-model="selectedValue"
            :input-id="inputId"
            :suggestions="suggestions"
            :disabled="disabled"
            :loading="isLoading"
            option-label="name"
            :min-length="3"
            :delay="500"
            fluid
            :invalid="!!error"
            :pt="{
                pcInputText: {
                    root: {
                        readonly: readonly,
                        class: 'font-mono',
                    },
                },
            }"
            @complete="onSearch"
            @option-select="onSelectCompany($event.value)"
            @clear="onClear"
        >
            <!-- ================================ -->
            <!-- TEMPLATE DES OPTIONS -->
            <!-- ================================ -->
            <template #option="{ option }">
                <ItemCompany :company="option.company" />
            </template>

            <!-- ================================ -->
            <!-- TEMPLATE VIDE -->
            <!-- ================================ -->
            <template #empty>
                <div class="px-3 py-2 text-sm text-gray-500">
                    Aucune entreprise trouvée pour ce SIREN
                </div>
            </template>
        </BaseAutoComplete>
    </BaseInputWrapper>
</template>
